import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '../ns-form/ns-form.js';
import {STATE_CONFIG} from './state-config.js';
import {STATE_TRANSLATIONS} from './state-translations.js';
import template from './ns-state.html';
import {FormMixin} from '../formMixin.js';

/**
 * Used when a state is needed to be selected.
 * This component validates if a field has been chosen.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>State Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-state></ns-state>
 *   </template>
 * </demo-snippet>
 */
export class NsState extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-state';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * stateList
             * Holds the list of states based on country
             */
            stateList: {
                type: Array,
                value: () => []
            },
            /**
             * country
             * Used to indicate country for localization
             */
            country: {
                type: String,
                value: "US",
                observer: '_initialize'
            },
            /**
             * language
             * Used to indicate language for localization
             */
            language: {
                type: String,
                value: "en",
                observer: '_initialize'
            },
            /**
             * label
             * Used to indicate label for localization
             */
            label: {
                type: String,
                value: 'State'
            },
            /**
             * The state selected in the dropdown.
             */
            state: {
                type: String,
                notify: true,
                value: '',
                reflectToAttribute: true
            },
            /**
             * Flag representing the dirty state of this component
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * flag for whether or not to sort state list
             */
            sortStateList: {
                type: Boolean,
                value: true
            },
            useServerTranslations: {
                type: Boolean,
                value: false
            },
            serverStateLabel: {
                type: String
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(stateDirty)'
        ];
    }

    /**
     * Ready Function
     * When component is initialized it will load the translations and state list
     * translations and the state list are pulled based on country and language
     */
    ready() {
        super.ready();
        this._initialize();
    }

    /**
     * Called when the country or language changes
     * @private
     */
    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            let serverTranslations = {
                label: this.serverStateLabel
            }
            this._localize(this.language, this.country, STATE_TRANSLATIONS, serverTranslations, this.useServerTranslations);

            //Set config will set the stateList
            this._setConfig(this.country, STATE_CONFIG);
            if (this.sortStateList) {
                //Sort the stateList
                this.stateList = this.stateList.sort((a, b) => (a.value > b.value) ? 1 : ((b.value > a.value) ? -1 : 0));
            }
        }
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        if (!(this.attributes.style && this.attributes.style.textContent.indexOf('display: none') > -1)) {
            return this.shadowRoot.querySelector("ns-form").validate();
        } else {
            return true;
        }
    }
}

customElements.define(NsState.is, NsState);
